import type { ReactNode } from 'react';
import type { SWRConfiguration } from 'swr';

import { mutate, SWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';

import { fetcher, endpoints } from './axios';

const swr: SWRConfiguration = {
  fetcher,
  revalidateOnReconnect: true,
  revalidateIfStale: false,
  revalidateOnFocus: false,
  shouldRetryOnError: false,
  errorRetryCount: 0,
};

type SwrProviderProps = {
  children: ReactNode;
};

type UsePaginatedMutationOptions = {
  onSuccess?: () => void | Promise<void>;
};

export function SwrProvider({ children }: SwrProviderProps) {
  return <SWRConfig value={swr}>{children}</SWRConfig>;
}

export async function clearRoleSpecificCache(): Promise<void> {
  await mutate(
    (key) => {
      if (key === endpoints.me.adminRoles) {
        return false;
      }
      // Skip permissions - they're refetched when assumedRoleId changes
      if (Array.isArray(key) && key.includes('assumed-role') && key.includes('permissions')) {
        return false;
      }
      return true;
    },
    undefined,
    { revalidate: true }
  );
}

export function usePaginatedMutation<TArg>(
  baseKey: string | null,
  mutationFn: (arg: TArg) => Promise<void>,
  options?: UsePaginatedMutationOptions
) {
  return useSWRMutation(
    baseKey,
    async (_url: string, { arg }: { arg: TArg }) => {
      await mutationFn(arg);
    },
    {
      onSuccess: async () => {
        if (baseKey) {
          await mutate((key) => typeof key === 'string' && key.startsWith(baseKey), undefined, {
            revalidate: true,
          });
        }
        await options?.onSuccess?.();
      },
    }
  );
}
